/**
 * Checkout Service
 *
 * API calls for managing checkout (kiosk) stations.
 * Uses path-based multi-tenancy: /api/orgs/{orgId}/checkouts
 *
 * Pagination contract matches the Device listing API:
 *   limit (1..200, default 50), offset (>=0, default 0),
 *   sortBy + sortOrder (asc|desc).
 */
import apiClient from './apiClient';

const checkoutService = {
  /**
   * List checkouts for an organization.
   * @param {number} orgId
   * @param {{ limit?: number, offset?: number, siteId?: number,
   *           sortBy?: string, sortOrder?: 'asc'|'desc' }} [opts]
   */
  listCheckouts: async (orgId, opts = {}) => {
    const params = {};
    if (opts.limit     != null) params.limit     = opts.limit;
    if (opts.offset    != null) params.offset    = opts.offset;
    if (opts.siteId    != null) params.siteId    = opts.siteId;
    if (opts.sortBy    != null) params.sortBy    = opts.sortBy;
    if (opts.sortOrder != null) params.sortOrder = opts.sortOrder;
    const response = await apiClient.get(`/api/orgs/${orgId}/checkouts`, { params });
    return response.data;
  },

  /**
   * Get one checkout.
   */
  getCheckout: async (orgId, checkoutId) => {
    const response = await apiClient.get(`/api/orgs/${orgId}/checkouts/${checkoutId}`);
    return response.data;
  },

  /**
   * Create a new checkout.
   * @param {number} orgId
   * @param {{ checkoutName: string, siteId: number, location: string,
   *           template?: 'STANDARD'|'OPEN_ACCESS'|'HIGH_SECURITY',
   *           description?: string }} payload
   */
  createCheckout: async (orgId, payload) => {
    const response = await apiClient.post(`/api/orgs/${orgId}/checkouts`, payload);
    return response.data;
  },

  /**
   * Update a checkout (partial / PATCH).
   * Accepts: checkoutName, siteId, location, description, template, status, dockCount.
   * Status is ONLINE or OFFLINE; each ONLINE PATCH is also a kiosk heartbeat
   * and refreshes the checkout's lastSyncAt value.
   */
  updateCheckout: async (orgId, checkoutId, payload) => {
    const response = await apiClient.patch(`/api/orgs/${orgId}/checkouts/${checkoutId}`, payload);
    return response.data;
  },

  /**
   * Delete a checkout.
   */
  deleteCheckout: async (orgId, checkoutId) => {
    await apiClient.delete(`/api/orgs/${orgId}/checkouts/${checkoutId}`);
  },
};

export default checkoutService;

